import { router } from "@inertiajs/react";
import { AlertCircle, CheckCircle, CircleAlert, Globe, Loader2 } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface DomainInfo {
  id: number;
  hostname: string;
  verificationStatus: "unverified" | "verified" | "pending" | "failed";
  verificationRecordName: string;
  verificationRecordValue: string;
  verificationHostname: string;
  verificationCheckedAt?: string | null;
  verifiedAt?: string | null;
}

interface DomainClaim {
  id: number;
  status: "pending" | "verified" | "failed" | "expired" | "canceled";
  hostname: string;
  recordName: string;
  recordValue: string;
  expiresAt?: string | null;
}

interface DomainStepProps {
  domain: DomainInfo | null;
  claim: DomainClaim | null;
  leadDomain?: string | null;
  onDomainConnected: () => void;
}

const DOMAIN_EXAMPLES = [
  { example: "example.com", description: "Your main domain" },
  { example: "blog.example.com", description: "A specific subdomain" },
  {
    example: "*.example.com",
    description: "Wildcard for all subdomains and main domain",
  },
];

export function DomainStep({ domain: domainProps, claim, leadDomain, onDomainConnected }: DomainStepProps) {
  const [domain, setDomain] = useState(leadDomain ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [copiedRecordName, setCopiedRecordName] = useState(false);
  const [copiedRecordValue, setCopiedRecordValue] = useState(false);

  const hasDomain = Boolean(domainProps?.id);
  const isVerified = domainProps?.verificationStatus === "verified";
  const isVerificationFailed = domainProps?.verificationStatus === "failed";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) {
      setErrorMessage("Domain is required");
      setSubmitStatus("error");
      return;
    }

    router.post(
      "/domains",
      { hostname: domain.trim(), from_onboarding: true },
      {
        forceFormData: true,
        preserveState: true,
        onStart: () => {
          setIsSubmitting(true);
          setSubmitStatus("idle");
          setErrorMessage("");
        },
        onSuccess: (page) => {
          const flash = (page.props as { flash?: { notice?: string; alert?: string } }).flash;
          if (flash?.notice) {
            setDomain("");
            setSubmitStatus("success");
            return;
          }
          if (flash?.alert) {
            setErrorMessage(flash.alert);
            setSubmitStatus("error");
          }
        },
        onError: (errors) => {
          setErrorMessage(errors.hostname || "Failed to register domain");
          setSubmitStatus("error");
        },
        onFinish: () => setIsSubmitting(false),
      },
    );
  };

  const handleVerify = () => {
    if (!domainProps?.id) return;
    setIsVerifying(true);
    router.post(
      `/domains/${domainProps.id}/verify`,
      { from_onboarding: true },
      {
        preserveState: true,
        onFinish: () => setIsVerifying(false),
      },
    );
  };

  if (isVerified) {
    onDomainConnected();
  }

  return (
    <div className="mx-auto w-full max-w-lg">
      <div className="mb-5 text-center sm:mb-8">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 sm:mb-4 sm:h-14 sm:w-14 sm:rounded-2xl dark:bg-orange-500/20">
          <Globe className="h-5 w-5 text-orange-600 sm:h-7 sm:w-7 dark:text-orange-400" />
        </div>
        <h1 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-3xl dark:text-white">
          Connect your domain
        </h1>
        <p className="mt-1.5 text-xs text-neutral-500 sm:mt-2 sm:text-sm dark:text-neutral-400">
          Add your domain and verify ownership with a DNS TXT record.
        </p>
      </div>

      {hasDomain && !isVerified ? (
        <div className="space-y-4 rounded-2xl border border-orange-100 bg-white p-4 shadow-sm sm:space-y-5 sm:p-6 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Verify {domainProps?.hostname}</h2>
            <Badge variant="outline">Unverified</Badge>
          </div>

          {isVerificationFailed ? (
            <Alert variant="destructive" className="border-amber-200 bg-amber-50 text-amber-900">
              <CircleAlert className="h-4 w-4" />
              <AlertTitle>Verification failed</AlertTitle>
              <AlertDescription>DNS record not found. Please check your DNS settings and try again.</AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive" className="border-amber-200 bg-amber-50 text-amber-900">
              <CircleAlert className="h-4 w-4" />
              <AlertTitle>Verification required</AlertTitle>
              <AlertDescription>Add the TXT record below to your DNS settings.</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm dark:border-neutral-700 dark:bg-neutral-800">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Host</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <code
                    className="flex cursor-pointer items-center justify-between gap-2 rounded bg-white px-2 py-1 font-mono text-xs hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                    onClick={() => {
                      void navigator.clipboard.writeText(domainProps?.verificationRecordName ?? "");
                      setCopiedRecordName(true);
                      setTimeout(() => setCopiedRecordName(false), 2000);
                    }}
                  >
                    <span>{domainProps?.verificationRecordName}</span>
                    {copiedRecordName && <CheckCircle className="h-4 w-4 text-green-500" />}
                  </code>
                </TooltipTrigger>
                <TooltipContent>{copiedRecordName ? "Copied!" : "Click to copy"}</TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Answer / Value</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <code
                    className="flex cursor-pointer items-center justify-between gap-2 rounded bg-white px-2 py-1 font-mono text-xs hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                    onClick={() => {
                      void navigator.clipboard.writeText(domainProps?.verificationRecordValue ?? "");
                      setCopiedRecordValue(true);
                      setTimeout(() => setCopiedRecordValue(false), 2000);
                    }}
                  >
                    <span>{domainProps?.verificationRecordValue}</span>
                    {copiedRecordValue && <CheckCircle className="h-4 w-4 text-green-500" />}
                  </code>
                </TooltipTrigger>
                <TooltipContent>{copiedRecordValue ? "Copied!" : "Click to copy"}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          <Button onClick={handleVerify} disabled={isVerifying} className="w-full">
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking…
              </>
            ) : isVerificationFailed ? (
              "Try verification again"
            ) : (
              "Verify domain"
            )}
          </Button>
        </div>
      ) : !hasDomain ? (
        <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm sm:p-6 dark:border-neutral-800 dark:bg-neutral-900">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div className="space-y-2">
              <Label htmlFor="domain">Domain Name</Label>
              <Input
                id="domain"
                type="text"
                placeholder="example.com or *.example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={isSubmitting}
                className={submitStatus === "error" ? "border-red-500" : ""}
              />

              {submitStatus === "error" && (
                <div className="flex items-start gap-2 text-sm">
                  <AlertCircle className="mt-0.5 h-4 w-4 text-red-500" />
                  <span className="text-red-600">{errorMessage}</span>
                </div>
              )}

              {submitStatus === "success" && (
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-green-500" />
                  <span className="text-green-600">Domain registered! Redirecting…</span>
                </div>
              )}

              <div className="mt-3 space-y-1.5">
                <p className="text-xs text-neutral-400 dark:text-neutral-500">Examples:</p>
                {DOMAIN_EXAMPLES.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <code className="rounded bg-neutral-100 px-2 py-0.5 font-mono dark:bg-neutral-800">
                      {item.example}
                    </code>
                    <span className="text-neutral-400">{item.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting || !domain.trim()} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering…
                </>
              ) : (
                "Connect Domain"
              )}
            </Button>
          </form>
        </div>
      ) : null}

      {claim?.status === "pending" && (
        <div className="mt-6 rounded-2xl border border-orange-100 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <h3 className="font-semibold text-neutral-900 dark:text-white">Claim {claim.hostname}</h3>
          <p className="mt-1 text-sm text-neutral-500">
            This domain is currently unverified by another account. Add the TXT record below to claim ownership.
          </p>
          <div className="mt-4 grid gap-3 rounded-lg border p-4 text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-neutral-500">Record name</span>
              <code className="rounded bg-neutral-50 px-2 py-1 font-mono text-xs dark:bg-neutral-800">
                {claim.recordName}
              </code>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-neutral-500">Record value</span>
              <code className="rounded bg-neutral-50 px-2 py-1 font-mono text-xs break-all dark:bg-neutral-800">
                {claim.recordValue}
              </code>
            </div>
          </div>
          <Button
            onClick={() =>
              router.post(`/domain_claims/${claim.id}/verify`, {
                from_onboarding: true,
              })
            }
            className="mt-4"
          >
            Verify claim
          </Button>
        </div>
      )}
    </div>
  );
}
